"use client";

import { AnimatePresence, domAnimation, LazyMotion, m } from "framer-motion";
import { Lock, LogIn, Menu as MenuIcon, User } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import * as React from "react";
import { MagneticButton } from "@/components/custom/magnetic_button";
import { Menu } from "@/components/custom/navigation_menu";
import { Button } from "@/components/ui/button";
import {
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuTrigger,
} from "@/components/ui/context_menu";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "@/components/ui/sheet";
import { getHeroTheme } from "@/config/routes";
import { useUserAuth } from "@/contexts/user_auth_context";
import { PRICING_SECTION_HREF } from "@/lib/pricing-navigation";
import { SCROLL_LOCK_FULL_WIDTH_CLASS } from "@/lib/scroll-lock";
import { cn } from "@/lib/utils";

const SCROLL_THRESHOLD = 20;

const getHeaderScrolledSnapshot = () => {
	if (typeof window === "undefined") return false;
	return window.scrollY > SCROLL_THRESHOLD;
};

const subscribeToHeaderScroll = (onStoreChange: () => void) => {
	window.addEventListener("scroll", onStoreChange, { passive: true });
	return () => window.removeEventListener("scroll", onStoreChange);
};

const SiteHeader = () => {
	const router = useRouter();
	const pathname = usePathname();
	const [isOpen, setIsOpen] = React.useState(false);
	const [active, setActive] = React.useState<string | null>(null);
	const { isAuthenticated, isLoading: authLoading } = useUserAuth();
	const isScrolled = React.useSyncExternalStore(
		subscribeToHeaderScroll,
		getHeaderScrolledSnapshot,
		() => false,
	);

	const getHeaderConfig = () => {
		const heroTheme = getHeroTheme(pathname);
		const isDropdownOpen = active !== null;

		if (heroTheme === "dark") {
			return {
				className:
					isScrolled || isDropdownOpen
						? "bg-zinc-950 text-white"
						: "bg-transparent text-white",
				isDark: true,
			};
		}

		// Light theme (default)
		return {
			className:
				isScrolled || isDropdownOpen
					? "bg-white text-black"
					: "bg-transparent text-black",
			isDark: false,
		};
	};

	const { className: headerClass, isDark } = getHeaderConfig();

	return (
		<LazyMotion features={domAnimation}>
			<header
				className={cn(
					"fixed top-0 z-50 w-full transition-colors duration-300",
					SCROLL_LOCK_FULL_WIDTH_CLASS,
					headerClass,
				)}
			>
				<div className="container mx-auto flex h-20 items-center justify-between px-4 md:px-6">
					<div className="flex items-center">
						<ContextMenu>
							<ContextMenuTrigger asChild>
								<Link
									href="/"
									prefetch={true}
									className="mr-6 flex items-center space-x-1"
								>
									<span className="font-syne text-2xl font-bold uppercase tracking-tight">
										TheHighraysYT
									</span>
								</Link>
							</ContextMenuTrigger>
							<ContextMenuContent className="w-48">
								<ContextMenuItem
									onClick={() => router.push("/admin")}
									className="cursor-pointer"
								>
									<Lock className="mr-2 h-4 w-4" />
									Admin Login
								</ContextMenuItem>
							</ContextMenuContent>
						</ContextMenu>
					</div>

					<div className="hidden md:block h-full">
						<Menu setActive={setActive}>
							<Link
								href="/"
								prefetch={true}
								className="font-syne text-sm font-medium uppercase tracking-wide hover:text-primary transition-colors px-4 py-2"
							>
								Home
							</Link>

							<Link
								href={PRICING_SECTION_HREF}
								className="font-syne text-sm font-medium uppercase tracking-wide hover:text-primary transition-colors px-4 py-2"
							>
								Pricing
							</Link>

							<Link
								href="/portfolio"
								className="font-syne text-sm font-medium uppercase tracking-wide hover:text-primary transition-colors px-4 py-2"
							>
								Portfolio
							</Link>

							<Link
								href="/blog"
								className="font-syne text-sm font-medium uppercase tracking-wide hover:text-primary transition-colors px-4 py-2"
							>
								Blog
							</Link>

							<Link
								href="/about"
								className="font-syne text-sm font-medium uppercase tracking-wide hover:text-primary transition-colors px-4 py-2"
							>
								About
							</Link>

							<Link
								href="/contact"
								className="font-syne text-sm font-medium uppercase tracking-wide hover:text-primary transition-colors px-4 py-2"
							>
								Contact
							</Link>
						</Menu>
					</div>

					<div className="hidden items-center gap-4 md:flex">
						<Link
							href={
								!authLoading && isAuthenticated ? "/dashboard" : "/auth/login"
							}
							prefetch={true}
						>
							<MagneticButton className="w-[160px] justify-center rounded-3xl bg-primary px-6 py-2 font-syne text-sm font-bold uppercase text-primary-foreground transition-transform hover:scale-105 flex items-center gap-2 overflow-hidden">
								<AnimatePresence mode="wait">
									{!authLoading && isAuthenticated ? (
										<m.span
											key="dashboard"
											initial={{ opacity: 0, y: 10 }}
											animate={{ opacity: 1, y: 0 }}
											exit={{ opacity: 0, y: -10 }}
											transition={{ duration: 0.15 }}
											className="flex items-center gap-2"
										>
											<User className="h-3 w-3" />
											Dashboard
										</m.span>
									) : (
										<m.span
											key="login"
											initial={{ opacity: 0, y: 10 }}
											animate={{ opacity: 1, y: 0 }}
											exit={{ opacity: 0, y: -10 }}
											transition={{ duration: 0.15 }}
											className="flex items-center gap-2"
										>
											<LogIn className="h-3 w-3" />
											Login
										</m.span>
									)}
								</AnimatePresence>
							</MagneticButton>
						</Link>
					</div>

					<Sheet open={isOpen} onOpenChange={setIsOpen}>
						<SheetTrigger asChild className="md:hidden">
							<Button
								variant="ghost"
								size="icon"
								className={cn(
									"md:hidden",
									isDark && "text-white hover:bg-white/10 hover:text-white",
								)}
								onClick={() => setIsOpen(true)}
							>
								<MenuIcon className="h-6 w-6" />
								<span className="sr-only">Toggle menu</span>
							</Button>
						</SheetTrigger>
						<SheetContent
							side="right"
							className="w-full sm:w-[400px] p-0 pt-12"
						>
							<SheetHeader className="pb-8 px-6 text-left">
								<SheetTitle className="flex items-center w-full overflow-hidden text-left">
									<span className="font-syne text-2xl font-bold uppercase tracking-tight truncate">
										TheHighraysYT
									</span>
								</SheetTitle>
								<SheetDescription className="sr-only">
									Main site navigation and account actions.
								</SheetDescription>
							</SheetHeader>
							<nav className="flex h-full flex-col gap-6 overflow-y-auto pb-20 px-6">
								<Link
									href="/"
									prefetch={true}
									onClick={() => setIsOpen(false)}
									className="font-syne text-4xl font-bold uppercase tracking-tighter hover:text-primary transition-colors"
								>
									Home
								</Link>

								<div className="flex flex-col gap-3">
									<Link
										href={PRICING_SECTION_HREF}
										prefetch={true}
										onClick={() => setIsOpen(false)}
										className="font-syne text-4xl font-bold uppercase tracking-tighter hover:text-primary transition-colors"
									>
										Pricing
									</Link>
								</div>

								<Link
									href="/portfolio"
									onClick={() => setIsOpen(false)}
									className="font-syne text-4xl font-bold uppercase tracking-tighter hover:text-primary transition-colors"
								>
									Portfolio
								</Link>
								<Link
									href="/blog"
									onClick={() => setIsOpen(false)}
									className="font-syne text-4xl font-bold uppercase tracking-tighter hover:text-primary transition-colors"
								>
									Blog
								</Link>
								<Link
									href="/about"
									onClick={() => setIsOpen(false)}
									className="font-syne text-4xl font-bold uppercase tracking-tighter hover:text-primary transition-colors"
								>
									About
								</Link>
								<Link
									href="/contact"
									onClick={() => setIsOpen(false)}
									className="font-syne text-4xl font-bold uppercase tracking-tighter hover:text-primary transition-colors"
								>
									Contact
								</Link>

								<div className="mt-auto w-full pt-8 pb-8">
									{!authLoading && isAuthenticated ? (
										<Link
											href="/dashboard"
											prefetch={true}
											onClick={() => setIsOpen(false)}
											className="group relative flex w-full items-center justify-between overflow-hidden rounded-2xl border border-primary/20 bg-primary/5 px-6 py-5 backdrop-blur-md transition-[background-color,border-color,box-shadow,transform] hover:bg-primary/10 hover:border-primary/40 hover:shadow-[0_0_30px_-10px_var(--primary)] active:scale-[0.98]"
										>
											<div className="flex flex-col">
												<span className="font-syne text-lg font-bold uppercase text-foreground group-hover:text-primary transition-colors">
													Dashboard
												</span>
												<span className="font-inter text-xs text-muted-foreground">
													View your purchases
												</span>
											</div>
											<span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform group-hover:scale-110 shadow-lg shadow-primary/20">
												<User className="h-5 w-5" />
											</span>
										</Link>
									) : (
										<Link
											href="/auth/login"
											prefetch={true}
											onClick={() => setIsOpen(false)}
											className="group relative flex w-full items-center justify-between overflow-hidden rounded-2xl border border-primary/20 bg-primary/5 px-6 py-5 backdrop-blur-md transition-[background-color,border-color,box-shadow,transform] hover:bg-primary/10 hover:border-primary/40 hover:shadow-[0_0_30px_-10px_var(--primary)] active:scale-[0.98]"
										>
											<div className="flex flex-col">
												<span className="font-syne text-lg font-bold uppercase text-foreground group-hover:text-primary transition-colors">
													Login
												</span>
												<span className="font-inter text-xs text-muted-foreground">
													Sign in to your account
												</span>
											</div>
											<span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform group-hover:scale-110 group-hover:-rotate-45 shadow-lg shadow-primary/20">
												<LogIn className="h-5 w-5" />
											</span>
										</Link>
									)}
								</div>
							</nav>
						</SheetContent>
					</Sheet>
				</div>
			</header>
		</LazyMotion>
	);
};

export default SiteHeader;
